// src/host/workplace/stores/BreakStore.ts
import { create } from 'zustand';

export interface BreakDetails {
  duration: number; // Simplified - just number of minutes
  type: string; // 'Coffee', 'Meal', 'InCall', 'Meeting', 'Stretch', etc.
  reason: string; // User-provided reason/message
  startedAt: Date; // When break started
  return_estimate?: Date; // Expected return time
}

interface BreakStore {
  // State
  breakDetails: BreakDetails | null;
  isLoading: boolean;
  error: string | null;
  breakDialogRequested: boolean;

  // Actions
  updateBreakDetails: (details: BreakDetails | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
  reset: () => void;
  requestBreakDialog: () => void;
  clearBreakDialogRequest: () => void;

  // Computed getters
  isOnBreak: () => boolean;
  minutesRemaining: () => number;
  breakStatus: () => 'active' | 'inactive' | 'overdue';
}

export const useBreakStore = create<BreakStore>((set, get) => ({
  // Initial state
  breakDetails: null,
  isLoading: false,
  error: null,
  breakDialogRequested: false,

  // Actions
  updateBreakDetails: (details) =>
    set({
      breakDetails: details,
      error: null,
      ...(details === null && { breakDialogRequested: false }),
    }),

  setLoading: (loading) => set({ isLoading: loading }),

  setError: (error) => set({ error }),

  clearError: () => set({ error: null }),

  // Clears all break state — called on logout to ensure no stale break data persists
  reset: () =>
    set({ breakDetails: null, isLoading: false, error: null, breakDialogRequested: false }),

  requestBreakDialog: () => set({ breakDialogRequested: true }),

  clearBreakDialogRequest: () => set({ breakDialogRequested: false }),

  // Computed getters
  isOnBreak: () => {
    const { breakDetails } = get();
    return !!breakDetails;
  },

  minutesRemaining: () => {
    const { breakDetails } = get();
    if (!breakDetails?.return_estimate) return 0;

    const now = new Date().getTime();
    const target = breakDetails.return_estimate.getTime();
    const remaining = Math.max(Math.ceil((target - now) / 60000), 0);

    return remaining;
  },

  breakStatus: () => {
    const { breakDetails } = get();
    if (!breakDetails) return 'inactive';

    const remaining = get().minutesRemaining();
    return remaining > 0 ? 'active' : 'overdue';
  },
}));
